import { Target } from 'lucide-react';
import { type AnalyticsResponse } from '../../services/api/analytics';

interface Props {
  data: AnalyticsResponse | null;
  loading: boolean;
}

const intentMeta: Record<string, { label: string; color: string; bg: string }> = {
  general: { label: 'Allmän fråga', color: '#64748b', bg: 'bg-slate-500' },
  order_history: { label: 'Köphistorik', color: '#a855f7', bg: 'bg-purple-500' },
  order_tracking: { label: 'Order & leverans', color: '#2563eb', bg: 'bg-blue-600' },
  order_return: { label: 'Retur', color: '#0ea5e9', bg: 'bg-sky-500' },
  product_catalog: { label: 'Produktsök', color: '#10b981', bg: 'bg-emerald-500' },
  product_lookup: { label: 'Produktuppslag', color: '#22c55e', bg: 'bg-green-500' },
  product_followup: { label: 'Produktfråga', color: '#059669', bg: 'bg-emerald-600' },
  outfit_build_direct: { label: 'Outfitbygge', color: '#8b5cf6', bg: 'bg-violet-500' },
  outfit_recommendation_advice: { label: 'Outfitråd', color: '#7c3aed', bg: 'bg-violet-600' },
  general_styling_advice: { label: 'Stilråd', color: '#d946ef', bg: 'bg-fuchsia-500' },
  contact_support: { label: 'Kundtjänstärende', color: '#ef4444', bg: 'bg-red-500' },
  unknown: { label: 'Okänd', color: '#94a3b8', bg: 'bg-slate-400' },
  product_questions: { label: 'Produktfrågor', color: '#14b8a6', bg: 'bg-teal-500' },
  outfit: { label: 'Outfit', color: '#8b5cf6', bg: 'bg-violet-500' },
  outfit_recommendation: { label: 'Outfit-rek.', color: '#8b5cf6', bg: 'bg-violet-500' },
  order_ops: { label: 'Order-ops', color: '#3b82f6', bg: 'bg-blue-500' },
  purchase_history: { label: 'Köphistorik', color: '#a855f7', bg: 'bg-purple-500' },
  general_info: { label: 'General info', color: '#64748b', bg: 'bg-slate-500' },
  default_ai: { label: 'Default AI', color: '#f59e0b', bg: 'bg-amber-500' },
  compound_clarify: { label: 'Compound', color: '#ec4899', bg: 'bg-pink-500' },
};

const BRAIN_CASE_TYPE_MEASURED_FROM = '2026-08-24';

function metaFor(intent: string): { label: string; color: string; bg: string } {
  return (
    intentMeta[intent] ?? {
      label: intent,
      color: '#94a3b8',
      bg: 'bg-slate-400',
    }
  );
}

export const AnalyticsIntentBreakdown = ({ data, loading }: Props) => {
  const breakdown = data?.intents?.breakdown ?? [];
  const total = data?.intents?.totalOccurrences ?? 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <Target className="h-4 w-4" /> Intent-fördelning
        </div>
        {!loading && total > 0 && (
          <span className="text-xs text-slate-500">{total.toLocaleString('sv-SE')} turns</span>
        )}
      </div>

      {loading && <div className="text-sm text-slate-400">Laddar…</div>}

      {!loading && breakdown.length === 0 && (
        <div className="text-sm text-slate-400">
          Ingen intent-data för perioden. Intent-typer lagras i{' '}
          <code className="text-xs">_debug.routing</code> per turn sedan Steg 1.
        </div>
      )}

      {!loading && breakdown.length > 0 && (
        <div className="space-y-2">
          {breakdown.slice(0, 10).map((row) => {
            const meta = metaFor(row.intentType);
            return (
              <div key={row.intentType} className="flex items-center gap-3">
                <div className="w-40 flex items-center gap-2 flex-shrink-0">
                  <div
                    className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: meta.color }}
                  />
                  <span
                    className="text-xs font-medium text-slate-700 truncate"
                    title={row.intentType}
                  >
                    {meta.label}
                  </span>
                </div>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${meta.bg}`}
                    style={{ width: `${Math.min(row.percentage, 100)}%` }}
                  />
                </div>
                <div className="w-28 text-right text-xs text-slate-600 flex-shrink-0">
                  <span className="font-semibold text-slate-800">
                    {row.count.toLocaleString('sv-SE')}
                  </span>
                  <span className="text-slate-400"> · {row.percentage}%</span>
                </div>
              </div>
            );
          })}
          {breakdown.length > 10 && (
            <div className="text-xs text-slate-400 pt-1">
              +{breakdown.length - 10} övriga intent-typer
            </div>
          )}
          <div className="text-[11px] text-slate-400 pt-2 border-t border-slate-100">
            Hjärnvägens ärendetyper mäts från och med {BRAIN_CASE_TYPE_MEASURED_FROM} — serien har
            ingen historik före det datumet.
          </div>
        </div>
      )}

      {!loading && (data?.llmTelemetry?.byWorkspace?.length ?? 0) > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-200">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Hjärnans arbetsytor (anrop)
          </div>
          <div className="space-y-1.5">
            {(data!.llmTelemetry!.byWorkspace ?? []).slice(0, 10).map((row) => {
              const max = data!.llmTelemetry!.byWorkspace![0]?.callCount ?? 1;
              return (
                <div key={row.workspace} className="flex items-center gap-3">
                  <span
                    className="w-40 text-xs font-medium text-slate-700 truncate"
                    title={row.workspace}
                  >
                    {row.workspace}
                  </span>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-slate-500"
                      style={{ width: `${Math.min((row.callCount / max) * 100, 100)}%` }}
                    />
                  </div>
                  <span className="w-16 text-right text-xs font-semibold text-slate-800">
                    {row.callCount.toLocaleString('sv-SE')}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
